import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  Bars3Icon,
  XMarkIcon,
  UserMinusIcon,
  BugAntIcon,
  ChartBarIcon,
  ClockIcon,
  EnvelopeIcon,
  CreditCardIcon,
  UserGroupIcon,
  UsersIcon,
  FireIcon,
  CameraIcon,
  CloudIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
  ChatBubbleLeftEllipsisIcon,
  FunnelIcon,
  DevicePhoneMobileIcon,
  MapPinIcon,
  KeyIcon,
  BanknotesIcon,
  AdjustmentsHorizontalIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { feedbackAPI, userManagementAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { subscribeToUntriagedCaseCount } from '../utils/caseStatsEvents';

export default function Sidebar() {
  const { admin, isOwner, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [untriagedCaseCount, setUntriagedCaseCount] = useState(0);
  const [abuseAlertCount, setAbuseAlertCount] = useState(0);
  const untriagedCaseCountGenerationRef = useRef(0);

  // Fetch sidebar badge counts on mount and periodically (every 5 minutes)
  useEffect(() => {
    const fetchUntriagedCaseCount = async () => {
      const requestGeneration = ++untriagedCaseCountGenerationRef.current;
      try {
        const response = await feedbackAPI.getStats();
        if (requestGeneration !== untriagedCaseCountGenerationRef.current) return;
        setUntriagedCaseCount(response.data?.data?.byStatus?.new ?? 0);
      } catch (err) {
        console.debug('[Sidebar] Failed to fetch untriaged case count:', err.message);
      }
    };
    const fetchAbuseAlertCount = async () => {
      try {
        const response = await userManagementAPI.getAbuseAlerts({ status: 'new' });
        setAbuseAlertCount(response.data?.alerts?.length || 0);
      } catch (err) {
        console.debug('[Sidebar] Failed to fetch abuse alert count:', err.message);
      }
    };
    const applyPublishedUntriagedCaseCount = (count) => {
      untriagedCaseCountGenerationRef.current += 1;
      setUntriagedCaseCount(count);
    };
    const unsubscribeUntriagedCaseCount = subscribeToUntriagedCaseCount(applyPublishedUntriagedCaseCount);
    fetchUntriagedCaseCount();
    fetchAbuseAlertCount();
    const interval = setInterval(() => {
      fetchUntriagedCaseCount();
      fetchAbuseAlertCount();
    }, 300000);
    return () => {
      untriagedCaseCountGenerationRef.current += 1;
      clearInterval(interval);
      unsubscribeUntriagedCaseCount();
    };
  }, []);

  const navItems = [
    {
      section: 'Main',
      items: [
        { to: '/', icon: HomeIcon, label: 'Dashboard' },
      ],
    },
    {
      section: 'Support',
      items: [
        { to: '/feedback', icon: ChatBubbleLeftEllipsisIcon, label: 'Cases', caseBadge: true },
        { to: '/member-reports', icon: FlagIcon, label: 'Member Reports' },
        { to: '/user-management', icon: UsersIcon, label: 'User Management', abuseBadge: true },
        ...(isOwner ? [{ to: '/access', icon: KeyIcon, label: 'Fold Access' }] : []),
      ],
    },
    {
      section: 'Analytics',
      items: [
        { to: '/founder-dashboard', icon: ChartBarIcon, label: 'Founder Dashboard' },
        { to: '/app-usage', icon: DevicePhoneMobileIcon, label: 'App Usage' },
        { to: '/revenue', icon: BanknotesIcon, label: 'Revenue' },
        { to: '/trials', icon: CalendarDaysIcon, label: 'Trials' },
        { to: '/onboarding', icon: FunnelIcon, label: 'Onboarding' },
        { to: '/outreach', icon: EnvelopeIcon, label: 'Outreach' },
        { to: '/church-demand', icon: MapPinIcon, label: 'Church Demand' },
        { to: '/feature-analytics', icon: SparklesIcon, label: 'Feature Analytics' },
        { to: '/sendgrid', icon: EnvelopeIcon, label: 'SendGrid' },
        { to: '/stripe', icon: CreditCardIcon, label: 'Stripe' },
        { to: '/user-engagement', icon: UserGroupIcon, label: 'User Engagement' },
        { to: '/firestore', icon: FireIcon, label: 'Firestore' },
        { to: '/rekognition', icon: CameraIcon, label: 'Rekognition' },
        { to: '/r2-storage', icon: CloudIcon, label: 'R2 Storage' },
        { to: '/compliance', icon: ShieldCheckIcon, label: 'Compliance' },
        { to: '/alerts', icon: BellAlertIcon, label: 'Alerts' },
      ],
    },
    {
      section: 'Monitoring',
      items: [
        { to: '/leakhunter', icon: BugAntIcon, label: 'LeakHunter' },
        { to: '/cron-jobs', icon: ClockIcon, label: 'Cron Jobs' },
        ...(isOwner ? [{ to: '/limits', icon: AdjustmentsHorizontalIcon, label: 'Limits' }] : []),
        ...(isOwner ? [{ to: '/flockbot-spend', icon: CurrencyDollarIcon, label: 'Flock Bot spend' }] : []),
      ],
    },
    {
      section: 'Data Management',
      items: [
        { to: '/persona-seed', icon: UserGroupIcon, label: 'Persona Seeding' },
        { to: '/user-deletion', icon: UserMinusIcon, label: 'User Deletion' },
      ],
    },
  ];

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      {/* Mobile hamburger button - fixed to top left, visible only on mobile */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="md:hidden fixed top-4 left-4 z-50 min-w-touch min-h-touch flex items-center justify-center bg-gradient-button text-cloud-white rounded-lg shadow-button transition-all active:scale-95"
        aria-label="Toggle navigation menu"
      >
        {isMobileMenuOpen ? (
          <XMarkIcon className="w-6 h-6" strokeWidth={2} />
        ) : (
          <Bars3Icon className="w-6 h-6" strokeWidth={2} />
        )}
      </button>

      {/* Backdrop overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-deep-navy/50 z-30 transition-opacity"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - responsive with mobile slide-in */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-40
          w-64 flex flex-col
          bg-cloud-white
          shadow-md
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        style={{
          background: 'linear-gradient(180deg, #E0E1DD 0%, #d5d7d3 100%)',
        }}
      >
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 pt-6">
          {navItems.map((section, idx) => (
            <div key={idx} className="mb-6 last:mb-0">
              {/* Section heading */}
              <h3 className="text-[12px] leading-[1.5] font-semibold text-storm-blue uppercase tracking-wider mb-2 px-3">
                {section.section}
              </h3>

              {/* Section items */}
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    onClick={closeMobileMenu}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 min-h-touch rounded-xl text-[15px] leading-[1.6] font-medium transition-all duration-300 ${
                        isActive
                          ? 'bg-gradient-button text-cloud-white shadow-md'
                          : 'text-deep-navy hover:bg-white/50 hover:shadow-sm'
                      }`
                    }
                  >
                    {/* Icon with proper sizing */}
                    <item.icon className="w-6 h-6 flex-shrink-0" strokeWidth={2} />
                    <span className="flex-1">{item.label}</span>
                    {/* Untriaged case badge */}
                    {item.caseBadge && untriagedCaseCount > 0 && (
                      <span
                        className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[24px] text-center"
                        aria-label={`${untriagedCaseCount} untriaged cases`}
                      >
                        {untriagedCaseCount > 99 ? '99+' : untriagedCaseCount}
                      </span>
                    )}
                    {/* Abuse alert badge */}
                    {item.abuseBadge && abuseAlertCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[24px] text-center">
                        {abuseAlertCount > 99 ? '99+' : abuseAlertCount}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Sign Out - bottom of sidebar */}
        <div className="p-4 border-t border-storm-blue/20">
          <div className="mb-2 px-4 text-xs text-storm-blue">
            <div className="truncate font-medium text-deep-navy">{admin?.name || admin?.email}</div>
            <div className="truncate">{admin?.email}</div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-4 py-3 min-h-touch rounded-xl text-[15px] leading-[1.6] font-medium text-deep-navy hover:bg-white/50 hover:shadow-sm transition-all duration-300"
          >
            <ArrowRightOnRectangleIcon className="w-6 h-6 flex-shrink-0" strokeWidth={2} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
